import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, RefreshCw, Eye, Send, XCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { useNfseList } from '@/features/fiscal/hooks/useNfse';
import { PeriodoFiltro, usePeriodo } from '@/components/listas/PeriodoFiltro';
import { CabecalhoOrdenavel, useOrdenacao } from '@/components/listas/CabecalhoOrdenavel';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import type { NfseListItem } from '@/features/fiscal/services/nfseService';

const PAGE_SIZES = [10, 30, 50] as const;

const fmt = (v: number) =>
  v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// A data de emissão é só o dia (coluna date): formata sem converter fuso, senão vira a véspera.
const fmtDate = (iso: string) =>
  iso ? iso.slice(0, 10).split('-').reverse().join('/') : '—';

function statusBadge(item: NfseListItem) {
  if (item.isCancelada === 1)
    return <Badge className="bg-red-500/10 text-red-600">Cancelada</Badge>;
  if (item.sChaveNota)
    return <Badge className="bg-green-500/10 text-green-600">Autorizada</Badge>;
  if (item.statusErro === 'AGUARDANDO_TRANSMISSAO')
    return <Badge className="bg-blue-500/10 text-blue-600">Aguardando</Badge>;
  return <Badge className="bg-yellow-500/10 text-yellow-600">Pendente</Badge>;
}

export default function NotasServico() {
  const navigate = useNavigate();
  const [pagina, setPagina] = useState(1);
  const [tamanho, setTamanho] = useState(30);
  const [busca, setBusca] = useState('');
  const [situacao, setSituacao] = useState('todos');
  const [cancelandoNota, setCancelandoNota] = useState<NfseListItem | null>(null);
  const [motivo, setMotivo] = useState('');
  // Abre nas notas de hoje, mais recentes primeiro: com o tempo a lista inteira fica grande demais.
  const periodo   = usePeriodo('hoje', () => setPagina(1));
  const ordenacao = useOrdenacao<'emissao' | 'numero' | 'rps'>('emissao', true, () => setPagina(1));

  // A busca vai ao servidor: espera a digitação parar.
  const [buscaEnviada, setBuscaEnviada] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setBuscaEnviada(busca.trim()), 400);
    return () => clearTimeout(t);
  }, [busca]);

  const { notas, total, isLoading, isFetching, refetch, transmitir, cancelar, isTransmitindo, isCancelando } =
    useNfseList(pagina, tamanho, {
      busca: buscaEnviada,
      situacao,
      ...periodo.intervalo,
      ordenarPor: ordenacao.ordenarPor,
      decrescente: ordenacao.decrescente,
    });

  const totalPaginas = Math.max(1, Math.ceil(total / tamanho));

  // Busca, situação e período já vêm filtrados do servidor.
  const notasFiltradas = notas;

  const handleCancelar = () => {
    if (!cancelandoNota || motivo.trim().length < 15) return;
    cancelar({ id: cancelandoNota.id, motivo });
    setCancelandoNota(null);
    setMotivo('');
  };

  return (
    <div className="flex flex-col h-full p-6 gap-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">NFS-e — Notas Fiscais de Serviço</h1>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={`mr-2 h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
          <Button size="sm" onClick={() => navigate('/fiscal/nfse/nova')}>
            <Plus className="mr-2 h-4 w-4" />
            Nova NFS-e
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[12rem] max-w-sm">
          <Label className="text-xs text-muted-foreground">Busca</Label>
          <Input
            placeholder="Nome, CNPJ/CPF, RPS, número..."
            value={busca}
            onChange={e => { setBusca(e.target.value); setPagina(1); }}
          />
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <PeriodoFiltro estado={periodo} rotuloData="Emissão" />
        </div>
        <Select value={situacao} onValueChange={(v) => { setSituacao(v); setPagina(1); }}>
          <SelectTrigger className="w-full sm:w-44" aria-label="Situação">
            <SelectValue placeholder="Todas" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todas as situações</SelectItem>
            <SelectItem value="autorizada">Autorizada</SelectItem>
            <SelectItem value="aguardando">Aguardando</SelectItem>
            <SelectItem value="pendente">Pendente</SelectItem>
            <SelectItem value="cancelada">Cancelada</SelectItem>
          </SelectContent>
        </Select>
        <div>
          <Label className="text-xs text-muted-foreground">Por página</Label>
          <select
            className="h-9 rounded-md border border-input bg-transparent px-3 py-1 text-sm"
            value={tamanho}
            onChange={e => { setTamanho(+e.target.value); setPagina(1); }}
          >
            {PAGE_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      {/* Tabela */}
      <div className="flex-1 overflow-auto rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <CabecalhoOrdenavel coluna="rps" ordenacao={ordenacao} className="w-16">RPS</CabecalhoOrdenavel>
              <CabecalhoOrdenavel coluna="numero" ordenacao={ordenacao} className="w-20">Nº Nota</CabecalhoOrdenavel>
              <CabecalhoOrdenavel coluna="emissao" ordenacao={ordenacao} className="w-24">Emissão</CabecalhoOrdenavel>
              <TableHead>Tomador</TableHead>
              <TableHead className="w-36">CNPJ/CPF</TableHead>
              <TableHead className="w-28 text-right">Valor</TableHead>
              <TableHead className="w-28">Status</TableHead>
              <TableHead className="w-28 text-center">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                  Carregando...
                </TableCell>
              </TableRow>
            )}
            {!isLoading && notasFiltradas.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                  {periodo.periodo === 'hoje' ? 'Nenhuma NFS-e emitida hoje — mude o período para ver as anteriores.'
                    : 'Nenhuma NFS-e encontrada.'}
                </TableCell>
              </TableRow>
            )}
            {notasFiltradas.map(nota => (
              <TableRow key={nota.id}>
                <TableCell className="font-mono">{nota.rps}</TableCell>
                <TableCell className="font-mono">{nota.numeroNota || '—'}</TableCell>
                <TableCell>{fmtDate(nota.dtEmissao)}</TableCell>
                <TableCell className="max-w-[200px] truncate">{nota.nomeRazao ?? '—'}</TableCell>
                <TableCell className="font-mono text-xs">{nota.cnpjCpf ?? '—'}</TableCell>
                <TableCell className="text-right font-medium">R$ {fmt(nota.valorNota)}</TableCell>
                <TableCell>{statusBadge(nota)}</TableCell>
                <TableCell>
                  <div className="flex gap-1 justify-center">
                    <Button
                      size="icon"
                      variant="ghost"
                      title="Detalhe"
                      onClick={() => navigate(`/fiscal/nfse/${nota.id}`)}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    {nota.isCancelada !== 1 && !nota.sChaveNota && (
                      <Button
                        size="icon"
                        variant="ghost"
                        title="Transmitir"
                        disabled={isTransmitindo}
                        onClick={() => transmitir(nota.id)}
                      >
                        <Send className="h-4 w-4 text-blue-600" />
                      </Button>
                    )}
                    {nota.isCancelada !== 1 && (
                      <Button
                        size="icon"
                        variant="ghost"
                        title="Cancelar"
                        disabled={isCancelando}
                        onClick={() => { setCancelandoNota(nota); setMotivo(''); }}
                      >
                        <XCircle className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Paginação */}
      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span>{total} notas no total</span>
        <div className="flex items-center gap-2">
          <Button size="icon" variant="outline" disabled={pagina <= 1} onClick={() => setPagina(p => p - 1)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span>Página {pagina} de {totalPaginas}</span>
          <Button size="icon" variant="outline" disabled={pagina >= totalPaginas} onClick={() => setPagina(p => p + 1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Dialog cancelamento */}
      <Dialog open={!!cancelandoNota} onOpenChange={open => !open && setCancelandoNota(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancelar NFS-e</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Tomador: <strong>{cancelandoNota?.nomeRazao}</strong>
            </p>
            <div>
              <Label>Motivo do cancelamento (mínimo 15 caracteres)</Label>
              <Textarea
                rows={3}
                value={motivo}
                onChange={e => setMotivo(e.target.value)}
                placeholder="Informe o motivo..."
              />
              {motivo.length > 0 && motivo.length < 15 && (
                <p className="text-xs text-destructive mt-1">
                  Mínimo 15 caracteres ({motivo.length}/15)
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelandoNota(null)}>Fechar</Button>
            <Button
              variant="destructive"
              disabled={motivo.trim().length < 15 || isCancelando}
              onClick={handleCancelar}
            >
              Confirmar Cancelamento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
